/**
 * Health Dashboard Column Definitions
 *
 * This file contains reusable column definitions for the health dashboard table.
 * Column sets can be shared across multiple pathologies (e.g., PSM uses HTA Protocol columns,
 * PEDIATRIC uses ICC-style columns).
 *
 * Each column set is a factory function that accepts (navigate, t) parameters
 * to support navigation and internationalization.
 */

import { Box, Chip, IconButton } from "@mui/material";

// Shared styles for all dashboard columns
export const DASHBOARD_STYLES = {
  correctChip: {
    backgroundColor: "rgba(158, 219, 160, 0.40)",
    color: "#38753A",
  },
  wrongChip: {
    backgroundColor: "rgba(227, 143, 143, 0.20)",
    color: "#D04444",
  },
  yellowChip: {
    backgroundColor: "rgba(255, 140, 0, 0.2)",
    color: "#FF8C00",
  },
};

// ============================================================================
// Reusable Column Builders
// ============================================================================

/**
 * Creates a fullname column with consistent styling
 */
const createFullnameColumn = (t) => ({
  id: "fullname",
  accessorKey: "fullname",
  header: t("name"),
  key: "fullname",
  size: 100,
  Cell: (cell) => (
    <Box sx={{ fontSize: "13px", fontWeight: 600, color: "#203482" }}>
      {cell.row.original.fullname}
    </Box>
  ),
});

/**
 * Creates an antiquity column
 */
const createAntiquityColumn = (t, size = 80) => ({
  id: "antiquity",
  accessorKey: "antiquity",
  header: t("antiquity"),
  key: "antiquity",
  size,
});

/**
 * Creates an actions column with view and start-chat buttons.
 * `openChat` opens the shared chat modal (see useChatModal) for the row's patient id.
 */
const createActionsColumn = (t, navigate, openChat, id = "actions") => ({
  id,
  accessorKey: "actions",
  header: t("actions"),
  size: 80,
  enableColumnActions: false,
  enableColumnFilter: false,
  enableSorting: false,
  Cell: (cell) => (
    <Box sx={{ display: "flex" }}>
      <IconButton
        onClick={() => navigate(`/patients/${cell.row.original.id}`)}
        aria-label="view"
      >
        <img src="/view.png" alt="view" />
      </IconButton>
      <IconButton
        onClick={() => openChat(cell.row.original.id)}
        aria-label={t("begin_chat")}
        title={t("begin_chat")}
      >
        <img src="/chat-cta.png" alt={t("begin_chat")} />
      </IconButton>
    </Box>
  ),
});

/**
 * Creates a weight column
 */
const createWeightColumn = (t, size = 10) => ({
  id: "weight",
  accessorKey: "weight",
  enableColumnActions: false,
  enableColumnFilter: false,
  enableSorting: false,
  header: t("weight"),
  size,
  Cell: (cell) => (
    <Box sx={{ marginLeft: "15%" }}>{cell.row.original.weight}</Box>
  ),
});

// ============================================================================
// ICC-Style Column Set (Used by ICC and PEDIATRIC pathologies)
// ============================================================================

/**
 * Returns ICC-style column configuration
 * Used by: ICC, PEDIATRIC
 */
export const getICCStyleColumns = (navigate, t, openChat) => [
  createFullnameColumn(t),
  createAntiquityColumn(t),
  {
    id: "FEVI",
    accessorFn: (row) => row?.FEVI?.value,
    header: "FEVI",
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original?.FEVI?.value > 40
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.yellowChip
        }
        label={
          cell.row.original?.FEVI?.value != null
            ? cell.row.original?.FEVI?.value + "%"
            : "--"
        }
      />
    ),
  },
  {
    id: "drugs",
    accessorKey: "drugs",
    header: t("amount_drugs"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 100,
    Cell: (cell) => (
      <Box
        sx={{
          fontSize: "13px",
          fontWeight: 600,
          color: "#203482",
          marginLeft: "15%",
        }}
      >
        {cell.row.original.drugs}
      </Box>
    ),
  },
  {
    id: "weekly_adherence",
    accessorKey: "weekly_adherence",
    header: t("weekly_adherence"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Chip
        sx={[
          cell.row.original.weekly_adherence < 40
            ? DASHBOARD_STYLES.wrongChip
            : cell.row.original.weekly_adherence <= 70
            ? DASHBOARD_STYLES.yellowChip
            : DASHBOARD_STYLES.correctChip,
          { marginLeft: "25%" },
        ]}
        label={
          cell.row.original.weekly_adherence != null
            ? cell.row.original.weekly_adherence + "%"
            : "--"
        }
      />
    ),
  },
  {
    id: "last_data_loaded",
    accessorKey: "last_data_loaded",
    header: t("last_load"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
  },
  {
    id: "hr_today",
    accessorKey: "hr_today",
    header: t("fc_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.hr_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.hr_today ? cell.row.original.hr_today.value : "-"
        }
      />
    ),
  },
  {
    id: "bp_weekly_average",
    accessorKey: "bp_weekly_average",
    header: t("ta_weekly_average"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Chip
        sx={[
          cell.row.original.bp_weekly_average?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip,
          { marginLeft: "25%" },
        ]}
        label={cell.row.original.bp_weekly_average?.value}
      />
    ),
  },
  {
    id: "face_today",
    accessorKey: "face_today",
    header: t("face_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) =>
      cell.row.original.face_today ? (
        <img
          style={{ marginLeft: "10%" }}
          src={`/face${cell.row.original?.face_today}.png`}
        />
      ) : (
        <></>
      ),
  },
  createActionsColumn(t, navigate, openChat),
];

// ============================================================================
// HTA Protocol Column Set (Used by HTA Protocol and PSM pathologies)
// ============================================================================

/**
 * Returns HTA Protocol column configuration
 * Used by: HTA (Protocol modality), PSM
 */
export const getHTAProtocolColumns = (navigate, t, openChat) => [
  createFullnameColumn(t),
  createAntiquityColumn(t, 50),
  createWeightColumn(t),
  {
    id: "measurement_dates",
    accessorFn: (row) => row?.bp_values?.measurement_dates?.value,
    header: "Fecha mediciones (desde / hasta)",
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Box sx={{ marginLeft: "25%" }}>
        {cell.row.original.bp_values?.measurement_dates?.value}
      </Box>
    ),
  },
  {
    id: "count_measurements",
    accessorKey: "count_measurements",
    header: t("measurement_quantity"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    muiTableBodyCellProps: {
      align: "center",
    },
  },
  {
    id: "bp_morning_average",
    accessorFn: (row) => row?.bp_values?.bp_morning_average,
    header: t("ta_average_day"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Chip
        sx={[
          cell.row.original.bp_values?.bp_weekly_average?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip,
          { marginLeft: "15%" },
        ]}
        label={cell.row.original.bp_values?.bp_morning_average?.value ?? "--"}
      />
    ),
  },
  {
    id: "bp_afternoon_average",
    accessorFn: (row) => row?.bp_values?.bp_afternoon_average,
    header: t("ta_average_afernoon"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Chip
        sx={[
          cell.row.original.bp_values?.bp_weekly_average?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip,
          { marginLeft: "15%" },
        ]}
        label={cell.row.original.bp_values?.bp_afternoon_average?.value ?? "--"}
      />
    ),
  },
  {
    id: "bp_general_average",
    accessorFn: (row) => row?.bp_values?.bp_weekly_average,
    header: t("ta_average_general"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Chip
        sx={[
          cell.row.original.bp_values?.bp_weekly_average?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip,
          { marginLeft: "15%" },
        ]}
        label={cell.row.original.bp_values?.bp_weekly_average?.value ?? "--"}
      />
    ),
  },
  {
    id: "hr_morning_afternoon",
    accessorFn: (row) => row?.hr_values?.hr_morning_afternoon,
    header: t("hr_morning_afternoon"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    muiTableBodyCellProps: {
      align: "center",
    },
  },
  {
    id: "hr_general_average",
    accessorFn: (row) => row?.hr_values?.hr_general_average,
    header: t("hr_general_average"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    muiTableBodyCellProps: {
      align: "center",
    },
  },
  createActionsColumn(t, navigate, openChat, "act"),
];

// ============================================================================
// HTA Maintenance Column Set (Used by HTA Maintenance modality)
// ============================================================================

/**
 * Returns HTA Maintenance column configuration
 * Used by: HTA (Maintenance modality)
 */
export const getHTAMaintenanceColumns = (navigate, t, openChat) => [
  createFullnameColumn(t),
  createAntiquityColumn(t, 50),
  createWeightColumn(t),
  {
    id: "count_measurements",
    accessorKey: "count_measurements",
    header: t("measurement_quantity"),
    enableColumnActions: false,
    enableColumnFilter: false,
    size: 80,
    muiTableBodyCellProps: {
      align: "center",
    },
  },
  {
    id: "bp_average",
    accessorFn: (row) => row?.bp_values?.bp_average,
    header: t("bp_average"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    muiTableBodyCellProps: {
      align: "center",
    },
    muiTableHeadCellProps: {
      align: "center",
    },
  },
  {
    id: "hr_average",
    accessorFn: (row) => row?.hr_values?.hr_average,
    header: t("hr_average"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    muiTableBodyCellProps: {
      align: "center",
    },
  },
  {
    id: "bp_max",
    accessorFn: (row) => row?.bp_values?.bp_max,
    header: t("bp_max"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    muiTableBodyCellProps: {
      align: "center",
    },
  },
  {
    id: "hr_max",
    accessorFn: (row) => row?.hr_values?.hr_max,
    header: t("hr_max"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    muiTableBodyCellProps: {
      align: "center",
    },
    muiTableHeadCellProps: {
      align: "center",
    },
  },
  {
    id: "bp_values.sys_out",
    accessorFn: (row) => row?.bp_values?.sys_out,
    header: t("bp_sys_out"),
    enableColumnActions: false,
    enableColumnFilter: false,
    size: 80,
    muiTableBodyCellProps: {
      align: "center",
    },
  },
  {
    id: "bp_values.dias_out",
    accessorFn: (row) => row?.bp_values?.dias_out,
    header: t("bp_dias_out"),
    enableColumnActions: false,
    enableColumnFilter: false,
    size: 80,
    muiTableBodyCellProps: {
      align: "center",
    },
  },
  createActionsColumn(t, navigate, openChat, "acti"),
];

export const getPediatricColumns = (navigate, t, openChat) => [
  createFullnameColumn(t),
  createAntiquityColumn(t),
  {
    id: "weekly_adherence",
    accessorKey: "weekly_adherence",
    header: t("weekly_adherence"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Chip
        sx={[
          cell.row.original.weekly_adherence < 40
            ? DASHBOARD_STYLES.wrongChip
            : cell.row.original.weekly_adherence <= 70
            ? DASHBOARD_STYLES.yellowChip
            : DASHBOARD_STYLES.correctChip,
          { marginLeft: "25%" },
        ]}
        label={
          cell.row.original.weekly_adherence != null
            ? cell.row.original.weekly_adherence + "%"
            : "--"
        }
      />
    ),
  },
  {
    id: "last_data_loaded",
    accessorKey: "last_data_loaded",
    header: t("last_load"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
  },
  {
    id: "hr_today",
    accessorKey: "hr_today",
    header: t("fc_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.hr_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.hr_today ? cell.row.original.hr_today.value : "-"
        }
      />
    ),
  },
  {
    id: "respiratory_rate_today",
    accessorKey: "respiratory_rate_today",
    header: t("fr_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.respiratory_rate_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.respiratory_rate_today
            ? cell.row.original.respiratory_rate_today.value
            : "-"
        }
      />
    ),
  },
  {
    id: "oxygen_saturation_today",
    accessorKey: "oxygen_saturation_today",
    header: t("so_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.oxygen_saturation_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.oxygen_saturation_today
            ? cell.row.original.oxygen_saturation_today.value
            : "-"
        }
      />
    ),
  },
  {
    id: "face_today",
    accessorKey: "face_today",
    header: t("face_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) =>
      cell.row.original.face_today ? (
        <img
          style={{ marginLeft: "10%" }}
          src={`/face${cell.row.original?.face_today}.png`}
        />
      ) : (
        <></>
      ),
  },
  createActionsColumn(t, navigate, openChat),
];

// ============================================================================
// MGA Column Set (Monitoreo General de Agudos)
// ============================================================================

/**
 * Returns MGA (Monitoreo General de Agudos) column configuration
 * Shows columns based on parameterization at pathology/institution/medical group level
 * Used by: MGA pathology
 */
export const getMGAColumns = (navigate, t, openChat) => [
  createFullnameColumn(t),
  createAntiquityColumn(t),
  {
    id: "weekly_adherence",
    accessorKey: "weekly_adherence",
    header: t("weekly_adherence"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Chip
        sx={[
          cell.row.original.weekly_adherence < 40
            ? DASHBOARD_STYLES.wrongChip
            : cell.row.original.weekly_adherence <= 70
            ? DASHBOARD_STYLES.yellowChip
            : DASHBOARD_STYLES.correctChip,
          { marginLeft: "25%" },
        ]}
        label={
          cell.row.original.weekly_adherence != null
            ? cell.row.original.weekly_adherence + "%"
            : "--"
        }
      />
    ),
  },
  {
    id: "last_data_loaded",
    accessorKey: "last_data_loaded",
    header: t("last_load"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => {
      const value = cell.row.original.last_data_loaded;
      const isOld = value && value.includes("días") && parseInt(value) >= 2;
      const isTooOld = value === "Mayor a 2 meses";

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span style={{
            color: isTooOld || isOld ? "#D04444" : "#38753A"
          }}>
            {value || "--"}
          </span>
          {(isTooOld || isOld) && (
            <span style={{ color: "#D04444" }}>⚠</span>
          )}
        </Box>
      );
    },
  },
  {
    id: "hr_today",
    accessorKey: "hr_today",
    header: t("fc_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.hr_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.hr_today?.value != null
            ? cell.row.original.hr_today.value
            : "--"
        }
      />
    ),
  },
  {
    id: "bp_average_today",
    accessorKey: "bp_average_today",
    header: t("ta_average_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Chip
        sx={[
          cell.row.original.bp_average_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip,
          { marginLeft: "15%" },
        ]}
        label={cell.row.original.bp_average_today?.value ?? "--"}
      />
    ),
  },
  {
    id: "oxygen_saturation_today",
    accessorKey: "oxygen_saturation_today",
    header: t("so_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.oxygen_saturation_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.oxygen_saturation_today?.value != null
            ? cell.row.original.oxygen_saturation_today.value
            : "--"
        }
      />
    ),
  },
  {
    id: "weight_today",
    accessorKey: "weight_today",
    header: t("weight_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.weight_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.weight_today?.value != null
            ? cell.row.original.weight_today.value
            : "--"
        }
      />
    ),
  },
  {
    id: "peak_flow_today",
    accessorKey: "peak_flow_today",
    header: t("fem_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.peak_flow_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.peak_flow_today?.value != null
            ? cell.row.original.peak_flow_today.value
            : "--"
        }
      />
    ),
  },
  {
    id: "body_temperature_today",
    accessorKey: "body_temperature_today",
    header: t("tc_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.body_temperature_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.body_temperature_today?.value != null
            ? cell.row.original.body_temperature_today.value
            : "--"
        }
      />
    ),
  },
  {
    id: "respiratory_rate_today",
    accessorKey: "respiratory_rate_today",
    header: t("fr_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.respiratory_rate_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.respiratory_rate_today?.value != null
            ? cell.row.original.respiratory_rate_today.value
            : "--"
        }
      />
    ),
  },
  {
    id: "face_today",
    accessorKey: "face_today",
    header: t("face_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) =>
      cell.row.original.face_today ? (
        <img
          style={{ marginLeft: "10%" }}
          src={`/face${cell.row.original?.face_today}.png`}
          alt={`face ${cell.row.original?.face_today}`}
        />
      ) : (
        <Box sx={{ marginLeft: "10%", fontSize: "20px", color: "#999" }}>○</Box>
      ),
  },
  createActionsColumn(t, navigate, openChat),
];

// ============================================================================
// MGC Column Set (Monitoreo General de Crónicos)
// ============================================================================

/**
 * Returns MGC (Monitoreo General de Crónicos) column configuration
 * Shows columns based on parameterization at pathology/institution/medical group level
 * Used by: MGC pathology
 */
export const getMGCColumns = (navigate, t, openChat) => [
  createFullnameColumn(t),
  createAntiquityColumn(t),
  {
    id: "weekly_adherence",
    accessorKey: "weekly_adherence",
    header: t("weekly_adherence"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Chip
        sx={[
          cell.row.original.weekly_adherence < 40
            ? DASHBOARD_STYLES.wrongChip
            : cell.row.original.weekly_adherence <= 70
            ? DASHBOARD_STYLES.yellowChip
            : DASHBOARD_STYLES.correctChip,
          { marginLeft: "25%" },
        ]}
        label={
          cell.row.original.weekly_adherence != null
            ? cell.row.original.weekly_adherence + "%"
            : "--"
        }
      />
    ),
  },
  {
    id: "last_data_loaded",
    accessorKey: "last_data_loaded",
    header: t("last_load"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => {
      const value = cell.row.original.last_data_loaded;
      const isOld = value && value.includes("días") && parseInt(value) >= 2;
      const isTooOld = value === "Mayor a 2 meses";

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span style={{
            color: isTooOld || isOld ? "#D04444" : "#38753A"
          }}>
            {value || "--"}
          </span>
          {(isTooOld || isOld) && (
            <span style={{ color: "#D04444" }}>⚠</span>
          )}
        </Box>
      );
    },
  },
  {
    id: "hr_today",
    accessorKey: "hr_today",
    header: t("fc_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.hr_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.hr_today?.value != null
            ? cell.row.original.hr_today.value
            : "--"
        }
      />
    ),
  },
  {
    id: "bp_average_today",
    accessorKey: "bp_average_today",
    header: t("ta_average_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 80,
    Cell: (cell) => (
      <Chip
        sx={[
          cell.row.original.bp_average_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip,
          { marginLeft: "15%" },
        ]}
        label={cell.row.original.bp_average_today?.value ?? "--"}
      />
    ),
  },
  {
    id: "oxygen_saturation_today",
    accessorKey: "oxygen_saturation_today",
    header: t("so_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.oxygen_saturation_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.oxygen_saturation_today?.value != null
            ? cell.row.original.oxygen_saturation_today.value
            : "--"
        }
      />
    ),
  },
  {
    id: "weight_today",
    accessorKey: "weight_today",
    header: t("weight_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.weight_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.weight_today?.value != null
            ? cell.row.original.weight_today.value
            : "--"
        }
      />
    ),
  },
  {
    id: "peak_flow_today",
    accessorKey: "peak_flow_today",
    header: t("fem_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.peak_flow_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.peak_flow_today?.value != null
            ? cell.row.original.peak_flow_today.value
            : "--"
        }
      />
    ),
  },
  {
    id: "body_temperature_today",
    accessorKey: "body_temperature_today",
    header: t("tc_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.body_temperature_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.body_temperature_today?.value != null
            ? cell.row.original.body_temperature_today.value
            : "--"
        }
      />
    ),
  },
  {
    id: "respiratory_rate_today",
    accessorKey: "respiratory_rate_today",
    header: t("fr_today"),
    enableColumnActions: false,
    enableColumnFilter: false,
    enableSorting: false,
    size: 50,
    Cell: (cell) => (
      <Chip
        sx={
          cell.row.original.respiratory_rate_today?.normal
            ? DASHBOARD_STYLES.correctChip
            : DASHBOARD_STYLES.wrongChip
        }
        label={
          cell.row.original.respiratory_rate_today?.value != null
            ? cell.row.original.respiratory_rate_today.value
            : "--"
        }
      />
    ),
  },
  createActionsColumn(t, navigate, openChat),
];

// ============================================================================
// Column Set Registry
// ============================================================================

/**
 * Registry mapping column set names to their factory functions
 */
export const COLUMN_SETS = {
  "icc-style": getICCStyleColumns,
  "hta-protocol": getHTAProtocolColumns,
  "hta-maintenance": getHTAMaintenanceColumns,
  pediatric: getPediatricColumns,
  mga: getMGAColumns,
  mgc: getMGCColumns,
};

export default {
  DASHBOARD_STYLES,
  COLUMN_SETS,
  getICCStyleColumns,
  getHTAProtocolColumns,
  getHTAMaintenanceColumns,
  getPediatricColumns,
  getMGAColumns,
  getMGCColumns,
};
